import { RefObject } from "react";
import { useShallow } from "zustand/react/shallow";

import TableCellEmpty from "ds/components/Table/Row/CellEmpty";
import { selectVisibleColumns } from "ds/components/Table/store/useTableStore/selectors";
import TableSimpleCell from "ds/components/TableSimple/Cell";
import { TableSimpleStore } from "ds/components/TableSimple/types";

import SpaceDetailsAccessNameCell from "../components/AccessCells/NameCell";
import SpaceDetailsAccessOriginCell from "../components/AccessCells/OriginCell";
import SpaceDetailsAccessRoleCell from "../components/AccessCells/RoleCell";
import SpaceDetailsAccessRoleSummaryCell from "../components/AccessCells/RoleSummaryCell";
import { SpaceDetailsAccessSessionRoleBinding } from "../components/AccessCells/types";
import SpaceDetailsExpandableStickyRows, {
  ExpandableStickyRowsContext,
} from "../components/ExpandableStickyRows";
import { SPACE_USER_SESSIONS_COLUMN_ID } from "./constants";
import { SpaceDetailsUserSession } from "./types";

type SpaceDetailsUserSessionsRowsProps = {
  data: SpaceDetailsUserSession[];
  store: TableSimpleStore;
  tableRef: RefObject<HTMLDivElement | null>;
};

const getUserId = (user: SpaceDetailsUserSession) => user.id;
const getUserRoleBindings = (user: SpaceDetailsUserSession) => user.roleBindings;
const getRoleBindingId = (binding: SpaceDetailsAccessSessionRoleBinding) => binding.id;

const SpaceDetailsUserSessionsRows = ({
  data,
  store,
  tableRef,
}: SpaceDetailsUserSessionsRowsProps) => {
  const columns = store(useShallow(selectVisibleColumns));

  const renderParentCells = (
    user: SpaceDetailsUserSession,
    { isExpandable, isExpanded, onToggle }: ExpandableStickyRowsContext
  ) => {
    const singleBinding = user.roleBindings[0];

    return columns.map((column, i) => {
      const cellId = `${user.id}-${column.id}`;
      const cellLayoutProps = { id: cellId, isFirst: i === 0 };

      switch (column.id) {
        case SPACE_USER_SESSIONS_COLUMN_ID.NAME:
          // No href: a login-policy principal has no managed-user page to open.
          return (
            <SpaceDetailsAccessNameCell
              key={cellId}
              id={cellId}
              name={user.name}
              isExpandable={isExpandable}
              isExpanded={isExpanded}
              onToggle={onToggle}
            />
          );
        case SPACE_USER_SESSIONS_COLUMN_ID.ROLE:
          if (isExpandable) {
            return (
              <SpaceDetailsAccessRoleSummaryCell key={cellId} {...cellLayoutProps} item={user} />
            );
          }
          if (singleBinding) {
            return (
              <SpaceDetailsAccessRoleCell key={cellId} {...cellLayoutProps} item={singleBinding} />
            );
          }
          return <TableSimpleCell key={cellId} {...cellLayoutProps} />;
        case SPACE_USER_SESSIONS_COLUMN_ID.ORIGIN:
          // Same for every row, so the parent needs no summary variant.
          return <SpaceDetailsAccessOriginCell key={cellId} {...cellLayoutProps} />;
        default:
          return null;
      }
    });
  };

  const renderChildCells = (
    binding: SpaceDetailsAccessSessionRoleBinding,
    user: SpaceDetailsUserSession
  ) =>
    columns.map((column, i) => {
      const cellId = `${user.id}-${binding.id}-${column.id}`;
      const cellLayoutProps = { id: cellId, isFirst: i === 0 };

      switch (column.id) {
        case SPACE_USER_SESSIONS_COLUMN_ID.ROLE:
          return <SpaceDetailsAccessRoleCell key={cellId} {...cellLayoutProps} item={binding} />;
        case SPACE_USER_SESSIONS_COLUMN_ID.ORIGIN:
          return <SpaceDetailsAccessOriginCell key={cellId} {...cellLayoutProps} />;
        default:
          // Columns without a per-binding value (the name column) show a dash; the connector
          // line is drawn on the child row's first cell via CSS, whichever column that is.
          return (
            <TableSimpleCell key={cellId} id={cellId} role={i === 0 ? "rowheader" : undefined}>
              <TableCellEmpty />
            </TableSimpleCell>
          );
      }
    });

  return (
    <SpaceDetailsExpandableStickyRows
      data={data}
      tableRef={tableRef}
      getParentId={getUserId}
      getChildren={getUserRoleBindings}
      getChildId={getRoleBindingId}
      renderParentCells={renderParentCells}
      renderChildCells={renderChildCells}
    />
  );
};

export default SpaceDetailsUserSessionsRows;
